/**
 * Wrapper around `<OrderImageryModal>` that owns the side-effects MPV needs:
 * pulling the orderable-scenes list for the selected feature, switching the
 * order type, and closing. Extracted from the inline
 * `renderOrderImageryModal` helper in MPV's `view.tsx` so the modal's
 * coupling to MPV's state stays in one focused file.
 */

import * as I from 'immutable';
import React from 'react';

import OrderImageryModal, {VariableOrderType} from 'app/components/OrderImageryModal';
import {OrderImageryState} from 'app/components/OrderImagerySidebar';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiImageryContract, ApiProject} from 'app/modules/Remote/Project';
import {OrderableScenesLoader} from 'app/providers/OrderableScenesProvider';
import {CachedApiGetterOptions, StatusMaybe} from 'app/utils/hookUtils';
import {findSceneByOrderType} from 'app/utils/imageryUtils';

interface Props {
  selectedFeature: I.ImmutableOf<ApiFeature> | null;
  selectedProject: I.ImmutableOf<ApiProject>;
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  isOrderImageryModalOpen: boolean;
  sceneToOrder: I.ImmutableOf<ApiOrderableScene> | null;
  setSceneToOrder: (s: I.ImmutableOf<ApiOrderableScene> | null) => void;
  orderImageryState: OrderImageryState;
  previewScenes: readonly I.ImmutableOf<ApiOrderableScene>[];
  orderableScenesLoader: OrderableScenesLoader;
  getCurrentImageryContract: (
    projectId: string,
    opts?: CachedApiGetterOptions
  ) => StatusMaybe<ApiImageryContract | null>;
  closeOrderImageryModal: () => void;
}

export default function OrderImageryModalContainer({
  selectedFeature,
  selectedProject,
  organization,
  profile,
  isOrderImageryModalOpen,
  sceneToOrder,
  setSceneToOrder,
  orderImageryState,
  previewScenes,
  orderableScenesLoader,
  getCurrentImageryContract,
  closeOrderImageryModal,
}: Props) {
  if (!selectedFeature || !isOrderImageryModalOpen || !sceneToOrder) {
    return null;
  }

  const orderableScenesStatusMaybe = orderableScenesLoader(selectedFeature.get('id'));
  const orderableScenes = orderableScenesStatusMaybe.value;

  return (
    <OrderImageryModal
      role={profile.get('role')}
      selectedProjectId={selectedProject.get('id')}
      feature={selectedFeature}
      orderableScene={sceneToOrder}
      organization={organization}
      onClose={closeOrderImageryModal}
      projectName={selectedProject.get('name')}
      onOrderTypeChange={(orderType: VariableOrderType) => {
        if (!orderableScenes) return;

        const scene = findSceneByOrderType(
          orderableScenes,
          sceneToOrder.get('sensingTime'),
          sceneToOrder.get('sourceId'),
          orderType
        );

        if (scene) {
          setSceneToOrder(scene);
        }
      }}
      getCurrentImageryContract={(opts) =>
        getCurrentImageryContract(selectedProject.get('id'), opts)
      }
      subsetFeature={orderImageryState.orderSubset ? orderImageryState.subsetFeature : null}
      orderableScenes={orderableScenes}
      previewScenes={previewScenes}
      onPreviewSceneChange={setSceneToOrder}
    />
  );
}
